import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth, formatApiError } from "@/lib/auth";
import { Wrench, Lock, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back.");
      navigate("/admin", { replace: true });
    } catch (err) {
      toast.error("Login failed", {
        description: formatApiError(err?.response?.data?.detail) || err.message,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B132B] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-11 h-11 bg-[#D32F2F] flex items-center justify-center">
            <Wrench className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-display font-bold text-lg text-white">VETERAN'S AUTO</div>
            <div className="font-mono-label text-[#D32F2F]">Admin Console</div>
          </div>
        </div>

        <div className="bg-white border-l-4 border-[#D32F2F] p-8">
          <h1 className="font-display text-2xl md:text-3xl font-bold text-[#0B132B] mb-1">
            Shop Owner Login
          </h1>
          <p className="text-sm text-[#4A5568] mb-8">
            View and manage customer appointments and messages.
          </p>

          <form onSubmit={submit} data-testid="admin-login-form" className="space-y-5">
            <div>
              <Label htmlFor="l-email" className="font-mono-label text-[#0B132B] mb-2 block">
                Email
              </Label>
              <Input
                id="l-email"
                data-testid="admin-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@veteransautorepair.com"
                required
                className="rounded-none border-[#E2E8F0] focus-visible:ring-[#D32F2F] focus-visible:border-[#D32F2F] h-12"
              />
            </div>
            <div>
              <Label htmlFor="l-pw" className="font-mono-label text-[#0B132B] mb-2 block">
                Password
              </Label>
              <Input
                id="l-pw"
                data-testid="admin-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="rounded-none border-[#E2E8F0] focus-visible:ring-[#D32F2F] focus-visible:border-[#D32F2F] h-12"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#D32F2F] hover:bg-[#B71C1C] disabled:opacity-60 text-white font-semibold py-4 px-8 rounded-none transition-colors"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Lock className="w-5 h-5" strokeWidth={2.5} />
              )}
              {loading ? "Signing in..." : "Sign In"}
            </button>
          </form>
        </div>

        <a
          href="/"
          className="mt-6 block text-center text-sm text-white/60 hover:text-white"
        >
          ← Back to Website
        </a>
      </div>
    </div>
  );
}
